import { useState, useEffect, useMemo, useCallback } from "react"
import { Calendar, dateFnsLocalizer, View, SlotInfo } from "react-big-calendar"
import { format, parse, startOfWeek, getDay, addHours, startOfDay, endOfDay } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Calendar as CalendarIcon, Plus, RefreshCw, Link2, AlertCircle } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { fetchWithAuth } from "@/lib/fetchWithAuth"
import { useAuth } from "@/hooks/useAuth"
import { useIsMobile } from "@/hooks/use-mobile"
import { toast } from "sonner"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "./calendario-custom.css"
import { EventDialog } from "@/components/Calendario/EventDialog"
import { GoogleCalendarDialog } from "@/components/Calendario/GoogleCalendarDialog"
import { EventDetailsDialog } from "@/components/Calendario/EventDetailsDialog"

const locales = {
  "pt-BR": ptBR,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

export interface CalendarEvent {
  id: string
  title: string
  description?: string
  start: Date
  end: Date
  allDay?: boolean
  event_type: "task" | "meeting" | "vistoria" | "instalacao" | "other"
  location?: string
  attendees?: Array<{ name: string; email: string }>
  status: "pending" | "confirmed" | "cancelled" | "completed"
  google_calendar_id?: string
  reminder_minutes?: number
  color?: string
  recurrence_rule?: string
  user_id: string
}

export default function Calendario() {
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [view, setView] = useState<View>(isMobile ? "agenda" : "month")
  const [date, setDate] = useState(new Date())
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false)
  const [isGoogleDialogOpen, setIsGoogleDialogOpen] = useState(false)
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date } | null>(null)
  const [isGoogleConnected, setIsGoogleConnected] = useState(false)
  const [googleOauthConnected, setGoogleOauthConnected] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [loading, setLoading] = useState(true)

  // Carregar eventos do banco de dados
  const loadEvents = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      const { data, error } = await (supabase.from("calendar_events" as any) as any)
        .select("*")
        .eq("user_id", user.id)
        .order("start_time", { ascending: true })

      if (error) throw error

      const formattedEvents: CalendarEvent[] = (data as any[] || []).map((event) => ({
        id: event.id,
        title: event.title,
        description: event.description,
        start: new Date(event.start_time),
        end: new Date(event.end_time),
        allDay: event.all_day,
        event_type: event.event_type,
        location: event.location,
        attendees: event.attendees,
        status: event.status,
        google_calendar_id: event.google_calendar_id,
        reminder_minutes: event.reminder_minutes,
        color: event.color,
        recurrence_rule: event.recurrence_rule,
        user_id: event.user_id,
      }))

      setEvents(formattedEvents)
    } catch (error) {
      console.error("Erro ao carregar eventos:", error)
      toast.error("Erro ao carregar eventos do calendário")
    } finally {
      setLoading(false)
    }
  }, [user])

  const checkGoogleConnection = useCallback(async () => {
    if (!user) return

    try {
      const res = await fetchWithAuth("/api/google-calendar/status")
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        setIsGoogleConnected(false)
        setGoogleOauthConnected(false)
        return
      }
      setIsGoogleConnected(!!body.connected)
      setGoogleOauthConnected(!!body.oauthConnected)
    } catch (error) {
      console.error("Erro ao verificar conexão do Google:", error)
      setIsGoogleConnected(false)
      setGoogleOauthConnected(false)
    }
  }, [user])

  useEffect(() => {
    loadEvents()
    checkGoogleConnection()
  }, [loadEvents, checkGoogleConnection])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const google = params.get("google_calendar")
    if (!google) return

    if (google === "connected") {
      toast.success("Google Calendar conectado com sucesso.")
      void checkGoogleConnection()
    } else if (google === "error") {
      const code = params.get("code")
      toast.error(
        code
          ? `Falha ao conectar ao Google Calendar (${code}).`
          : "Falha ao conectar ao Google Calendar."
      )
    }

    params.delete("google_calendar")
    params.delete("code")
    const qs = params.toString()
    const path = window.location.pathname + (qs ? `?${qs}` : "")
    window.history.replaceState(null, "", path)
  }, [checkGoogleConnection])

  // Garante que a view do calendário é compatível com o modo atual (desktop/mobile)
  useEffect(() => {
    setView((currentView) => {
      if (isMobile) {
        // No mobile só permitimos "agenda" e "day"
        return currentView === "agenda" || currentView === "day" ? currentView : "agenda"
      }

      // No desktop, garantimos uma view padrão válida
      return currentView === "month" || currentView === "week" || currentView === "day" || currentView === "agenda"
        ? currentView
        : "month"
    })
  }, [isMobile])

  const syncWithGoogle = async () => {
    if (!user || !isGoogleConnected) {
      toast.error("Google Calendar não está conectado ou a sincronização está desligada.")
      return
    }

    setIsSyncing(true)
    try {
      const res = await fetchWithAuth("/api/google-calendar/sync", { method: "POST" })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(body.error || "Erro na sincronização")
      }
      await loadEvents()
      toast.success(
        `Sincronizado: ${body.imported ?? 0} novos, ${body.updated ?? 0} atualizados no app.`
      )
    } catch (error) {
      console.error("Erro ao sincronizar:", error)
      toast.error(
        error instanceof Error ? error.message : "Erro ao sincronizar com Google Calendar"
      )
    } finally {
      setIsSyncing(false)
    }
  }

  // Abrir diálogo para criar novo evento
  const handleSelectSlot = (slotInfo: SlotInfo) => {
    setSelectedSlot({ start: slotInfo.start, end: slotInfo.end })
    setSelectedEvent(null)
    setIsEventDialogOpen(true)
  }

  // Abrir diálogo para editar evento existente
  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setIsDetailsDialogOpen(true)
  }

  // Editar evento
  const handleEditEvent = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setSelectedSlot(null)
    setIsDetailsDialogOpen(false)
    setIsEventDialogOpen(true)
  }

  // Deletar evento
  const handleDeleteEvent = async (eventId: string) => {
    try {
      const ev = events.find((e) => e.id === eventId)
      if (isGoogleConnected && ev?.google_calendar_id) {
        const del = await fetchWithAuth(
          `/api/google-calendar/google-event/${encodeURIComponent(ev.google_calendar_id)}`,
          { method: "DELETE" }
        )
        if (!del.ok) {
          const t = await del.json().catch(() => ({}))
          console.warn("Não foi possível remover no Google:", t)
        }
      }

      const { error } = await (supabase.from("calendar_events" as any) as any)
        .delete()
        .eq("id", eventId)
        .eq("user_id", user?.id)

      if (error) throw error

      toast.success("Evento excluído com sucesso!")
      await loadEvents()
      setIsDetailsDialogOpen(false)
    } catch (error) {
      console.error("Erro ao deletar evento:", error)
      toast.error("Erro ao excluir evento")
    }
  }

  // Salvar evento (criar ou atualizar)
  const handleSaveEvent = async (eventData: Partial<CalendarEvent>) => {
    if (!user) return

    try {
      const dataToSave = {
        user_id: user.id,
        title: eventData.title,
        description: eventData.description,
        start_time: eventData.start?.toISOString(),
        end_time: eventData.end?.toISOString(),
        all_day: eventData.allDay,
        event_type: eventData.event_type,
        location: eventData.location,
        attendees: eventData.attendees,
        status: eventData.status,
        reminder_minutes: eventData.reminder_minutes,
        color: eventData.color,
        recurrence_rule: eventData.recurrence_rule,
      }

      let savedEventId: string | null = null

      if (selectedEvent) {
        const { error } = await (supabase.from("calendar_events" as any) as any)
          .update(dataToSave)
          .eq("id", selectedEvent.id)
          .eq("user_id", user.id)

        if (error) throw error
        savedEventId = selectedEvent.id
        toast.success("Evento atualizado com sucesso!")
      } else {
        const { data: inserted, error } = await (supabase.from("calendar_events" as any) as any)
          .insert([dataToSave])
          .select("id")
          .single()

        if (error) throw error
        savedEventId = inserted?.id ?? null
        toast.success("Evento criado com sucesso!")
      }

      if (isGoogleConnected && savedEventId) {
        const push = await fetchWithAuth("/api/google-calendar/push-event", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eventId: savedEventId }),
        })
        if (!push.ok) {
          const errBody = await push.json().catch(() => ({}))
          toast.warning(
            errBody.error
              ? `Evento salvo, mas não replicado no Google: ${errBody.error}`
              : "Evento salvo, mas a replicação no Google falhou."
          )
        }
      }

      await loadEvents()
      setIsEventDialogOpen(false)
    } catch (error) {
      console.error("Erro ao salvar evento:", error)
      toast.error("Erro ao salvar evento")
    }
  }

  // Cores personalizadas para tipos de eventos
  const eventStyleGetter = (event: CalendarEvent) => {
    let backgroundColor = "#3b82f6" // blue-500 padrão
    
    if (event.color) {
      backgroundColor = event.color
    } else {
      switch (event.event_type) {
        case "vistoria":
          backgroundColor = "#f59e0b" // amber-500
          break
        case "instalacao":
          backgroundColor = "#10b981" // emerald-500
          break
        case "meeting":
          backgroundColor = "#8b5cf6" // violet-500
          break
        case "task":
          backgroundColor = "#06b6d4" // cyan-500
          break
      }
    }

    if (event.status === "completed") {
      backgroundColor = "#6b7280" // gray-500
    } else if (event.status === "cancelled") {
      backgroundColor = "#ef4444" // red-500
    }

    return {
      style: {
        backgroundColor,
        borderRadius: "4px",
        opacity: 0.9,
        color: "white",
        border: "0px",
        display: "block",
      },
    }
  }

  // Estatísticas
  const stats = useMemo(() => {
    const now = new Date()
    const today = startOfDay(now)
    const endToday = endOfDay(now)

    return {
      total: events.length,
      today: events.filter(e => e.start >= today && e.start <= endToday).length,
      pending: events.filter(e => e.status === "pending").length,
      vistorias: events.filter(e => e.event_type === "vistoria").length,
      instalacoes: events.filter(e => e.event_type === "instalacao").length,
    }
  }, [events])

  // Garante que nunca passamos eventos indefinidos ou sem título para o calendário
  const safeEvents = useMemo(
    () =>
      (events || [])
        .filter((e): e is CalendarEvent => !!e)
        .map((e) => ({
          ...e,
          title: e.title || "(Sem título)",
        })),
    [events],
  )

  const messages = {
    allDay: "Dia inteiro",
    previous: "Anterior",
    next: "Próximo",
    today: "Hoje",
    month: "Mês",
    week: "Semana",
    day: "Dia",
    agenda: "Agenda",
    date: "Data",
    time: "Hora",
    event: "Evento",
    noEventsInRange: "Não há eventos neste período.",
    showMore: (total: number) => `+ ${total} mais`,
  }

  // Garante que a view passada para o calendário é sempre válida para o modo atual
  const safeView: View = useMemo(() => {
    if (isMobile) {
      return view === "agenda" || view === "day" ? view : "agenda"
    }
    return view
  }, [isMobile, view])

  return (
    <div className={`space-y-6 ${isMobile ? 'p-3' : 'p-6'}`}>
      {/* Header */}
      <div className={isMobile ? "space-y-3" : "flex items-center justify-between"}>
        <div>
          <h1 className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-foreground`}>Calendário</h1>
          {!isMobile && (
            <p className="text-muted-foreground">Gerencie seus compromissos e tarefas</p>
          )}
        </div>
        <div className={`flex ${isMobile ? 'flex-col w-full gap-2' : 'gap-2'}`}>
          <Button
            variant="outline"
            size={isMobile ? "sm" : "sm"}
            onClick={syncWithGoogle}
            disabled={!isGoogleConnected || isSyncing}
            className={isMobile ? "w-full justify-center gap-2" : "gap-2"}
          >
            <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
            {isMobile ? "Sincronizar" : "Sincronizar"}
          </Button>
          <Button
            variant="outline"
            size={isMobile ? "sm" : "sm"}
            onClick={() => setIsGoogleDialogOpen(true)}
            className={isMobile ? "w-full justify-center gap-2" : "gap-2"}
          >
            <Link2 className="h-4 w-4" />
            {isMobile
              ? googleOauthConnected
                ? "Google"
                : "Conectar"
              : googleOauthConnected
                ? "Configurar Google"
                : "Conectar Google"}
          </Button>
          <Button 
            onClick={() => {
              setSelectedEvent(null)
              setSelectedSlot({ start: new Date(), end: addHours(new Date(), 1) })
              setIsEventDialogOpen(true)
            }} 
            className={isMobile ? "w-full justify-center gap-2" : "gap-2"}
          >
            <Plus className="h-4 w-4" />
            {isMobile ? "Novo" : "Novo Evento"}
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Hoje</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.today}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Vistorias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{stats.vistorias}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Instalações</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-500">{stats.instalacoes}</div>
          </CardContent>
        </Card>
      </div>

      {/* Alerta de Google Calendar */}
      {!googleOauthConnected && (
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-900">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                  Conecte seu Google Calendar
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Conecte sua conta do Google para sincronizar eventos automaticamente entre os calendários.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsGoogleDialogOpen(true)}
                  className="mt-3 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"
                >
                  Conectar agora
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendário */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Calendário de Eventos
          </CardTitle>
          <CardDescription>
            Clique em um horário para criar um novo evento ou em um evento existente para visualizar detalhes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className={isMobile ? "h-[400px] flex items-center justify-center" : "h-[600px] flex items-center justify-center"}>
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">Carregando calendário...</p>
              </div>
            </div>
          ) : (
            <div className={isMobile ? "h-[400px]" : "h-[600px]"}>
              <Calendar
                localizer={localizer}
                events={safeEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: "100%" }}
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable
                view={safeView}
                onView={setView}
                date={date}
                onNavigate={setDate}
                eventPropGetter={eventStyleGetter}
                messages={messages}
                culture="pt-BR"
                views={isMobile ? { agenda: true, day: true } : undefined}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogos */}
      <EventDialog
        open={isEventDialogOpen}
        onOpenChange={setIsEventDialogOpen}
        onSave={handleSaveEvent}
        event={selectedEvent}
        defaultStart={selectedSlot?.start}
        defaultEnd={selectedSlot?.end}
      />

      <EventDetailsDialog
        open={isDetailsDialogOpen}
        onOpenChange={setIsDetailsDialogOpen}
        event={selectedEvent}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
      />

      <GoogleCalendarDialog open={isGoogleDialogOpen} onOpenChange={setIsGoogleDialogOpen} />
    </div>
  )
}
